import styled from '@emotion/styled'
import { useCallback, useContext, useEffect, useState } from 'react'
import catchError from '../../../components/global/CatchError'
import Loader from '../../../components/global/Loader'
import SwitchButton from '../../../components/global/SwitchButton'
import Title from '../../../components/global/Title'
import { sweetAlertSuccess } from '../../../components/sweetalert/Alert'
import Table from '../../../components/table/Table'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { useVerifyAdminToken } from '../../../hooks/useVerifyAdminToken'
import apiClient from '../../../services/apiClient'
import { DtoPlanning } from '../../../types/planning.type'
import { AdminContainer, LinkBtn, TitleWrapper } from '../users/AdminUsers'

const displaySessions = (sessions: string | undefined) => {
    if (!sessions) return '0 séance'

    const sessionNumber = JSON.parse(sessions).length

    return `${sessionNumber > 1 ? sessionNumber + ' séances' : sessionNumber + ' séance'}`
}

const AdminPlannings = () => {
    const user = useContext(UserContext)
    const [plannings, setPlannings] = useState<any[][] | undefined>(undefined)

    const verifyToken = useVerifyAdminToken()
    const lng = useLangSelected()
    const [currentLang, setCurrentLang] = useState(lng)

    const tableHeader = [
        '#',
        'Titre',
        'Heure début',
        'Heure fin',
        'Lundi',
        'Mardi',
        'Mercredi',
        'Jeudi',
        'Vendredi',
        'Samedi',
        'Dimanche',
        'Actif',
        'Ordre',
        '',
        '',
    ]

    const loadPlannings = useCallback(() => {
        verifyToken()
        apiClient
            .post('/plannings/all', { lng })
            .then(({ data }) => {
                setPlannings(
                    data.map((planning: DtoPlanning) => {
                        const isActive = (planning as any).isActive ?? true
                        return [
                            ['id', planning.id],
                            ['value', planning.name],
                            ['value', (planning.minTime as string) + 'h'],
                            ['value', (planning.maxTime as string) + 'h'],
                            ['value', displaySessions(planning.monday)],
                            ['value', displaySessions(planning.tuesday)],
                            ['value', displaySessions(planning.wednesday)],
                            ['value', displaySessions(planning.thursday)],
                            ['value', displaySessions(planning.friday)],
                            ['value', displaySessions(planning.saturday)],
                            ['value', displaySessions(planning.sunday)],
                            [
                                'render',
                                <SwitchWrapper key={`switch-${planning.id}`}>
                                    <SwitchButton
                                        checked={isActive}
                                        onChange={() => {
                                            verifyToken()
                                            apiClient
                                                .patch(`/plannings/${planning.id}`, { isActive: !isActive, lng })
                                                .then(() => loadPlannings())
                                                .catch((err) =>
                                                    catchError(err.response.data, () => {
                                                        localStorage.removeItem('tokenAdmin')
                                                        user.adminData = undefined
                                                        window.location.reload()
                                                    }),
                                                )
                                        }}
                                    />
                                </SwitchWrapper>,
                            ],
                            ['value', (planning as any).order ?? 0],
                            ['edit', `/${lng}/bva-admin/plannings/${planning.id}`],
                            ['remove', true],
                        ]
                    }),
                )
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    localStorage.removeItem('tokenAdmin')
                    user.adminData = undefined
                    window.location.reload()
                }),
            )
    }, [lng, verifyToken])

    useEffect(() => {
        if (!plannings || currentLang !== lng) {
            setCurrentLang(lng)
            loadPlannings()
        }
    }, [lng, loadPlannings])

    if (!plannings) return <Loader dataTestId="loader" />

    const deleteById = (id: number) => {
        verifyToken()
        apiClient
            .delete(`/plannings/${id}`)
            .then(({ data }) => {
                sweetAlertSuccess('La suppression de cette compétition est réussi').then(() => {
                    setPlannings([...plannings.filter((planning) => planning[0][1] !== data.id)])
                })
            })
            .catch((err) =>
                catchError(err.response.data, () => {
                    localStorage.removeItem('tokenAdmin')
                    user.adminData = undefined
                    window.location.reload()
                }),
            )
    }

    return (
        <>
            <TitleWrapper>
                <Title dataTestId="admin-plannings-title">Plannings</Title>
                <LinkBtn
                    to={`/${lng}/bva-admin/plannings/creer`}
                    className="btn btn-primary mb-2"
                    data-testid="admin-plannings-create-btn"
                >
                    Créer
                </LinkBtn>
            </TitleWrapper>
            <AdminContainer data-testid="admin-plannings-container">
                <Table
                    tableData={plannings}
                    tableHeader={tableHeader}
                    deleteById={deleteById}
                    data-testid="admin-plannings-table"
                />
            </AdminContainer>
        </>
    )
}

const SwitchWrapper = styled.div`
    width: 60px;
`

export default AdminPlannings
